import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import styles from './issue_details_view.module.css';
import { ActionButton, Content, Dialog, DialogContainer, Flex, Item, Link, Menu, MenuTrigger, Picker, ProgressCircle, Text, TextArea, ToastQueue, View } from '@adobe/react-spectrum';
import { invokeFinderApp } from '../../../../utils/NavigationUtils';
import { Check2, ChevronLeft } from 'react-bootstrap-icons';
import { FormViewComponent } from '../../../../components/protected/layout-components/FormView/FormViewComponent';
import { ActivityIconBlue, AlertIconFill, TrashIcon } from '../../../../components/protected/Icons/IconsLib';
import { SolutionStatusDropDown } from './SolutionStatusDropDown';
import IssueCustomFormView, { IIssueCustomFormViewHandle } from './IssueCustomFormView';
import { useTranslation } from 'react-i18next';
import { FPADataFactory, FPADataTypes } from '../../../../infra/protected/FPA/FPAData';
import IssueDocumentsView from './IssueDocumentsView';
import IssueDocumentUploadDialog from './IssueDocumentUploadDialog';
import IssueTimelineView from './IssueTimelineView';
import { ContextSelection } from '../../../../components/protected/Search/ContextSelection';
import type { ContextObject } from '../../../../components/protected/Search/ContextSelection/types';
import { usePreloadAssets } from '../../../../hooks/UsePreloadAssets';

interface IIssueDetailsViewProps {
  issue: any;
  handleBackClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  statusList?: any[];
  users?: Array<{ id: string; name: string }>;
  onStatusChange?: (statusIdent: string) => void;
  onUpdate?: (values: {
    docNo?: string;
    sendTo?: string;
    folderId?: string;
    projectId?: string;
    activityId?: string;
  }) => Promise<boolean>;
  isStatusUpdating?: boolean;
  compactMode?: boolean;
  copyContextId?: string;
  onRefreshIssue?: () => Promise<void> | void;
}

const IssueDetailsView: React.FC<IIssueDetailsViewProps> = ({ issue, handleBackClick, onEdit, onDelete, statusList = [], users = [], onStatusChange, onUpdate, isStatusUpdating = false, compactMode = false, copyContextId, onRefreshIssue }) => {
  const { t } = useTranslation('issue');
  const _t = (key: string) => t(`issue_detail.${key}`, { ns: 'issue' });
  const _tt = (key: string) => t(`issue_common.${key}`, { ns: 'issue' });
  const notesText = String(issue?.description || issue?.comments || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const customFormRef = useRef<IIssueCustomFormViewHandle>(null);
  const { folderStatusMapping, projectStatusMapping, activityStatusMapping } =
    usePreloadAssets();
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState('request');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [documentsRefreshKey, setDocumentsRefreshKey] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [docNo, setDocNo] = useState(issue?.refId || issue?.ident || '');
  const [sendTo, setSendTo] = useState<string | undefined>(undefined);
  const [issueContextSelection, setIssueContextSelection] =
    useState<ContextObject | null>(null);
  const [contextIds, setContextIds] = useState<{
    folderId: string;
    projectId: string;
    activityId: string;
  }>(() => ({
    folderId: String(issue?.folderId || ''),
    projectId: String(issue?.projectId || ''),
    activityId: String(issue?.activityId || ''),
  }));
  const createdText = issue?.created ? issue?.created : issue?.deadLine || '-';
  const createdDisplay = createdText && createdText !== '-'
    ? moment(createdText).isValid()
      ? moment(createdText).format('DD.MM.YYYY')
      : createdText
    : '-';
  const modifiedDisplay = issue?.modified
    ? moment(issue.modified).isValid()
      ? moment(issue.modified).format('DD.MM.YYYY')
      : issue.modified
    : '-';
  useEffect(() => {
    setDocNo(issue?.refId || issue?.ident || '');
  }, [issue?.id, issue?.refId, issue?.ident]);
  useEffect(() => {
    const nextSendTo = String(issue?.solverId || '').trim();
    setSendTo(nextSendTo || undefined);
  }, [issue?.id, issue?.solverId]);
  useEffect(() => {
    setContextIds({
      folderId: String(issue?.folderId || ''),
      projectId: String(issue?.projectId || ''),
      activityId: String(issue?.activityId || ''),
    });
  }, [issue?.activityId, issue?.folderId, issue?.projectId, issue?.id]);

  const getContextIdsFromSelection = useCallback(
    (selection: ContextObject | null) => {
      const next = {
        activityId: '',
        projectId: '',
        folderId: '',
      };

      if (!selection) return next;

      selection.pathSegmentContexts?.forEach((segment) => {
        const id = String(segment.id ?? '').trim();
        const type = String(segment.type ?? '').toUpperCase();
        if (!id) return;
        if (type === 'FOLDER') next.folderId = id;
        if (type === 'PROJECT') next.projectId = id;
        if (type === 'ACTIVITY') next.activityId = id;
      });

      const selectedId = String(selection.contextId ?? '').trim();
      const selectedType = String(selection.contextType ?? '').toUpperCase();
      if (selectedId) {
        if (selectedType === 'FOLDER') next.folderId = selectedId;
        if (selectedType === 'PROJECT') next.projectId = selectedId;
        if (selectedType === 'ACTIVITY') next.activityId = selectedId;
      }

      return next;
    },
    [],
  );

  const handleContextSelectionChange = useCallback(
    (selection: ContextObject | null) => {
      setIssueContextSelection(selection);
      const nextIds = getContextIdsFromSelection(selection);
      setContextIds(nextIds);
    },
    [getContextIdsFromSelection],
  );

  const contextSelectionDefaultValue = useMemo(() => {
    const raw = contextIds.activityId || contextIds.projectId || contextIds.folderId || '';
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [contextIds.activityId, contextIds.folderId, contextIds.projectId]);

  const contextSelectionFallbackValue = useMemo((): ContextObject | null => {
    const toPositiveId = (value: unknown): number | null => {
      const raw = String(value ?? '').trim();
      if (!raw || raw === '0') return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    };

    const folderId = toPositiveId(issue?.folderId);
    const projectId = toPositiveId(issue?.projectId);
    const activityId = toPositiveId(issue?.activityId);

    const folderName = String(issue?.folderName || '').trim();
    const projectName = String(issue?.projectName || '').trim();
    const activityName = String(issue?.activityName || '').trim();

    const pathSegments: string[] = [];
    const pathSegmentContexts: NonNullable<ContextObject['pathSegmentContexts']> = [];

    if (folderId && folderName) {
      pathSegments.push(folderName);
      pathSegmentContexts.push({ id: folderId, type: 'FOLDER' });
    }
    if (projectId && projectName) {
      pathSegments.push(projectName);
      pathSegmentContexts.push({ id: projectId, type: 'PROJECT' });
    }
    if (activityId && activityName) {
      pathSegments.push(activityName);
      pathSegmentContexts.push({ id: activityId, type: 'ACTIVITY' });
    }

    const leaf = pathSegmentContexts[pathSegmentContexts.length - 1];
    if (!leaf || pathSegments.length === 0) return null;

    return {
      contextId: leaf.id,
      contextType: leaf.type,
      link: '',
      pathSegments,
      pathSegmentContexts,
    };
  }, [
    issue?.activityId,
    issue?.activityName,
    issue?.folderId,
    issue?.folderName,
    issue?.projectId,
    issue?.projectName,
  ]);

  const onContextPathResolveError = useCallback((error: unknown) => {
    console.error('Issue context resolve failed', error);
  }, []);
  const copyUrl = () => {
    const linkId = compactMode ? String(copyContextId || '') : String(issue?.id || '');
    const basePath = compactMode ? '/finder' : window.location.pathname;
    const url = `${window.location.origin}${basePath}?id=${linkId}`;
    navigator.clipboard.writeText(url);
    ToastQueue.positive(_t('link_copied'), { timeout: 1000 });
  };
  const handleDeleteClicked = () => {
    setShowDeleteDialog(true);
  };
  const confirmDelete = () => {
    setShowDeleteDialog(false);
    onDelete?.();
  };
  const handleUpdate = async () => {
    if (isUpdating) {
      return;
    }

    setIsUpdating(true);
    try {
      const formUpdated = issue?.customFormId && customFormRef.current
        ? await customFormRef.current.submitForm()
        : true;
      const issueUpdated = onUpdate
        ? await onUpdate({
            docNo,
            sendTo,
            folderId: contextIds.folderId,
            projectId: contextIds.projectId,
            activityId: contextIds.activityId,
          })
        : true;

      if (formUpdated && issueUpdated) {
        ToastQueue.positive(_t('issue_updated'), { timeout: 1200 });
      } else {
        ToastQueue.negative(_t('unable_to_update_issue_form'), { timeout: 1500 });
      }
    } catch (error) {
      ToastQueue.negative(_t('unable_to_update_issue_form'), { timeout: 1500 });
    } finally {
      setIsUpdating(false);
    }
  };
  const sendToOptions = users.map((user) => ({ id: String(user.id), name: user.name }));
  const selectedSendToKey = sendToOptions.some((user) => user.id === String(sendTo || ''))
    ? String(sendTo)
    : undefined;
  const requestFooter = (
    <Flex alignItems="end" justifyContent="space-between" gap="size-200" width="100%" UNSAFE_className={styles.request_footer}>
      <Flex alignItems="end" gap="size-200" flex={1}>
        <View flex={1} minWidth="150px">
          <Text UNSAFE_className={styles.footer_label}>{_t('context')}:</Text>
          <View UNSAFE_className={styles.footer_field}>
            <ContextSelection
              key={`issue-context-${String(issue?.id || issue?.ident || '')}`}
              value={issueContextSelection || contextSelectionFallbackValue}
              onSelectionChange={handleContextSelectionChange}
              folderStatusMap={folderStatusMapping}
              projectStatusMap={projectStatusMapping}
              activityStatusMap={activityStatusMapping}
              defaultValue={contextSelectionDefaultValue}
              explicitContextId={
                issueContextSelection ? null : contextSelectionDefaultValue
              }
              onExplicitContextResolveError={onContextPathResolveError}
              allowClearSelection
              allowTransparentBG
              filledBackground={false}
              showBorders={false}
              aria-label={_t('context')}
            />
          </View>
        </View>
        <View flex={1} minWidth="150px">
          <Text UNSAFE_className={styles.footer_label}>{_t('send_to')}:</Text>
          <View UNSAFE_className={styles.footer_field}>
            <Picker
              aria-label={_t('send_to')}
              items={sendToOptions}
              selectedKey={selectedSendToKey}
              onSelectionChange={(key) => setSendTo(key ? String(key) : undefined)}
              placeholder={_t('select_user')}
              isQuiet
              isDisabled={sendToOptions.length === 0}
              width="100%"
            >
              {(item) => <Item key={item.id}>{item.name}</Item>}
            </Picker>
          </View>
        </View>
      </Flex>
      <ActionButton UNSAFE_className={styles.update_button} onPress={handleUpdate} isDisabled={isUpdating || !issue?.ident}>
        <span className={styles.update_button_icon}><Check2 size={16} /></span>
        <span>{isUpdating ? _t('updating') : _t('update')}</span>
      </ActionButton>
    </Flex>
  );

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const handleWheel = (event: WheelEvent) => {
      const scroller = root.closest('.issue-detail-scroll-host') as HTMLElement | null;
      if (!scroller) return;
      scroller.scrollTop += event.deltaY;
    };

    root.addEventListener('wheel', handleWheel, { passive: true });
    return () => root.removeEventListener('wheel', handleWheel);
  }, []);

  return (
    <div ref={rootRef} className={compactMode ? styles.compact_mode : ''}>
    <Flex width="100%" direction="column">
      <View UNSAFE_className={styles.header_main}>
        <View UNSAFE_className={styles.header_child}>
          <View UNSAFE_className={styles.frame_parent}>
            <Flex alignItems="start" justifyContent="space-between" width="100%" wrap="nowrap">
              <View UNSAFE_className={styles.icon_activity_parent}>
                {handleBackClick ? (
                  <ActionButton isQuiet onPress={handleBackClick} UNSAFE_className={styles.back_button}>
                    <ChevronLeft size={20} />
                  </ActionButton>
                ) : null}
                <ActivityIconBlue size={22} />
                <b title={issue?.title} className={styles.activity_name}>
                  {issue?.title?.length > 55 ? `${issue.title.substring(0, 55)}...` : issue?.title}
                </b>
              </View>
              <View UNSAFE_className={styles.icon_list_parent}>
                {statusList.length > 0 ? (
                  <Flex alignItems="center" gap="size-100">
                    <SolutionStatusDropDown
                      statusArray={statusList}
                      selectedStatus={issue?.status || ''}
                      onStatusChange={onStatusChange}
                      isDisabled={isStatusUpdating}
                    />
                    {isStatusUpdating ? <ProgressCircle aria-label="Updating status" size="S" isIndeterminate /> : null}
                  </Flex>
                ) : (
                  <View
                    UNSAFE_className={styles.footer_wrapper}
                    UNSAFE_style={{
                      backgroundColor: issue?.statusColor || '#eceff1',
                      color: '#fff',
                      width: 'auto',
                      minWidth: '90px',
                      paddingLeft: '12px',
                      paddingRight: '12px',
                    }}
                  >
                    <span className={styles.footer_text_1}>{issue?.statusName || '-'}</span>
                  </View>
                )}
                <View UNSAFE_className={styles.icon_list_child}>
                  <div className={styles.delete_icon} onClick={handleDeleteClicked}>
                    <TrashIcon size={32} />
                  </div>
                  <MenuTrigger>
                    <ActionButton UNSAFE_style={{ border: 'none' }}>...</ActionButton>
                    <Menu onAction={copyUrl}>
                      <Item key="copy">{_t('copy_link')}</Item>
                    </Menu>
                  </MenuTrigger>
                </View>
              </View>
            </Flex>
            {(issue?.folderName || issue?.projectName || issue?.activityName) && (
              <View UNSAFE_className={styles.folder_name_parent}>
                <View UNSAFE_className={styles.folder_name}>
                  {issue?.folderName ? <Link UNSAFE_className={styles.folder_name_link} onPress={() => invokeFinderApp(issue.folderId, 'folder')}>{issue.folderName}</Link> : null}
                  {issue?.folderName && (issue?.projectName || issue?.activityName) ? <span> &gt; </span> : null}
                  {issue?.projectName ? <Link UNSAFE_className={styles.folder_name_link} onPress={() => invokeFinderApp(issue.projectId, 'project')}>{issue.projectName}</Link> : null}
                  {issue?.projectName && issue?.activityName ? <span> &gt; </span> : null}
                  {issue?.activityName ? <Link UNSAFE_className={styles.folder_name_link} onPress={() => invokeFinderApp(issue.activityId, 'activity')}>{issue.activityName}</Link> : null}
                </View>
              </View>
            )}
            <View UNSAFE_className={styles.tag_parent} marginTop={compactMode ? '16px' : '28px'}>
              <Flex direction="column" gap="10px" alignItems="start">
                <Flex direction="row" gap="size-400" wrap>
                  <Content UNSAFE_style={{ fontSize: '14px', marginTop: '8px' }}>
                    <Flex direction="row" alignItems="center" justifyContent="center" UNSAFE_className={styles.footer_wrapper}>
                      <Flex direction="row" alignItems="center" justifyContent="center" gap="6px">
                        <View position="relative" UNSAFE_className={styles.footer_text_1}>
                          <span title={issue.entryTypeName}>{issue.entryTypeName || '-'}</span>
                        </View>
                        <View position="relative" width="2px" height="20px" UNSAFE_className={styles.pipe_sign}></View>
                        <div className={styles.footer_text_1}>{issue.refId || issue.ident || '-'}</div>
                      </Flex>
                    </Flex>
                  </Content>
                  <Flex direction="column" gap="0">
                    <Content UNSAFE_style={{ fontSize: '14px' }}>{_t('created')}</Content>
                    <Content UNSAFE_style={{ fontSize: '14px' }}>{createdDisplay}</Content>
                  </Flex>
                  <Flex direction="column" gap="0">
                    <Content UNSAFE_style={{ fontSize: '14px' }}>{_t('modified')}</Content>
                    <Content UNSAFE_style={{ fontSize: '14px' }}>{modifiedDisplay}</Content>
                  </Flex>
                </Flex>
              </Flex>
            </View>
          </View>
        </View>
      </View>
      <View marginTop={compactMode ? '16px' : '24px'} width="100%">
        <Flex UNSAFE_className={styles.tab_bar}>
          {[
            { key: 'request', label: _t('request') },
            { key: 'communication', label: _t('communication') },
            { key: 'documents', label: _t('documents') },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`${styles.tab_button} ${selectedTab === tab.key ? styles.tab_button_active : ''}`}
              onClick={() => setSelectedTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </Flex>

        {selectedTab === 'request' ? (
          <Flex direction="column" marginTop="size-100" UNSAFE_className={styles.request_tab_panel}>
            <Flex direction="column" gap="size-100" gridArea="detail" width="100%" UNSAFE_className={styles.request_form_scroll}>
              <View width="100%" UNSAFE_className={styles.request_content_width}>
                {issue.customFormId ? (
                  <IssueCustomFormView
                    ref={customFormRef}
                    formId={issue.customFormId}
                    formValuesId={issue.customFormValuesId}
                    remoteId={issue.remoteId}
                    remoteAcl={issue.remoteAcl}
                  />
                ) : issue.selectedFormContext ? (
                  <FormViewComponent
                    key={`issue-context-form-${issue.id}-${issue.selectedFormContext?.id || 'none'}`}
                    formIdType="self"
                    selectedItem={issue.selectedFormContext}
                  />
                ) : (
                  <View UNSAFE_style={{ padding: '16px', backgroundColor: '#f5f7f8', borderRadius: '8px' }}>
                    {_t('no_custom_form')}
                  </View>
                )}
              </View>
              {notesText ? (
                <Flex direction="column" gap="size-100" marginTop="size-200">
                  <TextArea
                    label={_t('notes')}
                    value={notesText}
                    isReadOnly
                    width="100%"
                    minHeight="size-1000"
                  />
                </Flex>
              ) : null}
            </Flex>
            {requestFooter}
          </Flex>
        ) : null}

        {selectedTab === 'communication' ? (
          <View UNSAFE_style={{ marginTop: '24px' }}>
            <IssueTimelineView
              issueId={String(issue?.ident || issue?.id || '')}
              messageContextId={String(issue?.activityId || issue?.projectId || issue?.folderId || '').trim() || undefined}
              notifyUser={{
                id: String(issue?.solverId || issue?.userId || ''),
                name: String(issue?.solver || issue?.userName || ''),
              }}
            />
          </View>
        ) : null}

        {selectedTab === 'documents' ? (
          <View UNSAFE_style={{ marginTop: '24px' }}>
            <IssueDocumentsView
              issue={issue}
              refreshKey={documentsRefreshKey}
              onUploadDocument={() => setShowUploadDialog(true)}
            />
          </View>
        ) : null}
      </View>
      <IssueDocumentUploadDialog
        isOpen={showUploadDialog}
        issue={issue}
        onClose={() => setShowUploadDialog(false)}
        onUploaded={async () => {
          await onRefreshIssue?.();
          setDocumentsRefreshKey((prev) => prev + 1);
        }}
      />
      <DialogContainer onDismiss={() => setShowDeleteDialog(false)}>
        {showDeleteDialog ? (
          <Dialog size="S" UNSAFE_className={styles.delete_pop_up_dialog} isDismissable={false}>
            <Flex UNSAFE_className={styles.delete_pop_up_parent}>
              <Content UNSAFE_className={styles.delete_pop_up_header}>{_t('delete_issue')}</Content>
              <View UNSAFE_className={styles.delete_pop_up_line}></View>
              <View UNSAFE_className={styles.delete_pop_up_warning_text}>{_t('delete_confirm_text')}</View>
              <View UNSAFE_className={styles.buttons_parent}>
                <View UNSAFE_className={styles.button_cancel}>
                  <Link
                    UNSAFE_className={styles.delete_pop_up_cancel_text}
                    isQuiet
                    onPress={() => setShowDeleteDialog(false)}
                  >
                    {_tt('button_cancel')}
                  </Link>
                </View>
                <View UNSAFE_className={styles.button_confirm}>
                  <Link
                    UNSAFE_className={styles.delete_pop_up_confirm_text}
                    isQuiet
                    onPress={confirmDelete}
                  >
                    {_tt('button_delete')}
                  </Link>
                </View>
              </View>
              <View UNSAFE_className={styles.ellipse_div}>
                <View UNSAFE_className={styles.delete_alert_icon}>
                  <AlertIconFill size={72} />
                </View>
              </View>
            </Flex>
          </Dialog>
        ) : null}
      </DialogContainer>
    </Flex>
    </div>
  );
};

export default IssueDetailsView;
